import { i18n } from '@/plugins/i18n';
import { convertCentsToDollarsDecimal, convertPercentageToDecimal } from '@/utils/helpers';
import { type ApiAthleteTag, AthleteTag, AthleteTagBase } from '@/types/tags';
import { cloneDeep, isNil } from 'lodash-es';
import type { WalletStatus, WalletVerificationStatus } from './paymentRequests';
import type { ApiPaginationParams } from './api';
import type { AcademicYear } from './academicYears';
import { safeParseDate } from '@/utils/paymentUtils';
import { getCurrentAcademicYear } from '@/utils/academicYear';
import { TeamPosition } from '@/types/positions';
import type { AthleteDisplayItemProps } from '@/components/common/AthleteDisplayItem.vue';
import type { ApiBudgetCategories } from './budgets.categorized';
import type { ApiBudgetAthleteCategorized } from './budgets.categorized';

export interface ApiBudgetAthleteLineItem {
  allocation: number;
  created_at?: string | null;
  has_roster_spot: boolean;
  nil_earnings: number | null;
  scholarship_allocation: number | null;
  scholarship_percentage: number;
  updated_at?: string | null;
}

export interface ApiBudgetAthleteScholarship {
  equivalency_percentage: number; // min 0, max 100
  package_twrn: string | null;
  award_categories: {
    twrn: string;
    amount: number;
  }[];
}
export const GENDERS = {
  UNSPECIFIED: 0,
  MALE: 1,
  FEMALE: 2,
  NON_BINARY: 3,
} as const;

export type Gender = typeof GENDERS[keyof typeof GENDERS];

export const ACADEMIC_YEAR_ENUM = {
  FRESHMAN: 0,
  REDSHIRT_FRESHMAN: 10,
  SOPHOMORE: 20,
  REDSHIRT_SOPHOMORE: 30,
  JUNIOR: 40,
  REDSHIRT_JUNIOR: 50,
  SENIOR: 60,
  REDSHIRT_SENIOR: 70,
  GRADUATE_STUDENT: 80,
} as const;
export type AcademicYearEnum = typeof ACADEMIC_YEAR_ENUM[keyof typeof ACADEMIC_YEAR_ENUM];

export interface ApiBudgetAthletePosition {
  name: string;
  is_default: boolean;
  order: number | null;
};

export interface ApiBudgetAthletePositionRef {
  uuid: string;
  order: number | null;
}

export const ATHLETE_REMOVAL_TYPE_ENUM = {
  FUTURE: 'future',
  FUTURE_AND_CURRENT: 'future_and_current',
} as const;

export type AthleteRemovalTypeEnum
  = typeof ATHLETE_REMOVAL_TYPE_ENUM[keyof typeof ATHLETE_REMOVAL_TYPE_ENUM];

export interface ApiBudgetAthlete {
  academic_year_end: string;
  academic_year_start: string;
  first_name: string;
  gender: Gender;
  jersey_number: string | null;
  is_active: boolean;
  last_name: string;
  // Legacy field. Kept optional for backward compatibility while API migrates
  positions?: ApiBudgetAthletePosition[];
  // New field: position reference (uuid + order)
  position?: ApiBudgetAthletePositionRef | null;
  line_item: ApiBudgetAthleteLineItem;
  scholarship: ApiBudgetAthleteScholarship;
  profile_photo: string | null;
  twrn: string;
  was_manually_created: boolean;
  tags: string[];
  removed_from_roster_at: string | null;
  axle_twrn: string | null;
  recruit_id: number | null;
}

export interface CreateAthleteRequestModel {
  first_name: string;
  last_name: string;
  position?: string;
  position_id?: string;
  allocation?: number;
  scholarship_percentage?: number;
  scholarship_allocation?: number;
  nil_earnings?: number;
  has_roster_spot: boolean;
  recruit_id?: number | null;
}

export interface UpdateAthleteScholarshipsRequestModel {
  athlete: string;
  scholarship_package: string | null;
  equivalency_percentage: number;
  award_categories: {
    twrn: string;
    amount: number;
  }[];
}

export class BudgetAthlete {
  academicYearEnd: string;
  academicYearStart: string;
  allocation: number;
  avatar: string | null;
  firstName: string;
  fullName: string;
  gender: Gender;
  jerseyNumber: string | null;
  isActive: boolean;
  lastName: string;
  nilEarnings: number;
  position: string;
  rosterSpot: boolean;
  scholarshipAllocation: number;
  scholarshipPercentage: number;
  twrn: string;
  wasManuallyCreated: boolean;
  tags: AthleteTag[];
  order: number | null;
  equivalencyPercentage: number;
  removedFromRosterAt: string | null;
  packageTwrn: string | null;
  scholarshipCalculatedValue: number;
  awardCategories: {
    twrn: string;
    amount: number;
  }[];

  axleTwrn: string | null;
  recruitId: number | null;

  constructor(data: ApiBudgetAthlete, tag_definitions: ApiAthleteTag[], teamTwrn?: string) {
    this.academicYearEnd = data.academic_year_end;
    this.academicYearStart = data.academic_year_start;
    this.allocation = convertCentsToDollarsDecimal(data.line_item?.allocation ?? 0);
    this.avatar = data.profile_photo;
    this.firstName = data.first_name;
    this.fullName = `${data.first_name} ${data.last_name}`;
    this.gender = data.gender;
    this.jerseyNumber = data.jersey_number;
    this.isActive = data.is_active;
    this.lastName = data.last_name;
    this.nilEarnings = convertCentsToDollarsDecimal(data.line_item?.nil_earnings ?? 0);
    // Resolve position/order from UUID
    const resolvedFromUuid = teamTwrn ? TeamPosition.resolveName(teamTwrn, data.position?.uuid ?? null) : null;
    this.position = resolvedFromUuid ?? i18n.global.t('unspecified');
    this.order = data.position?.order ?? null;
    this.rosterSpot = data.line_item?.has_roster_spot ?? true;
    this.scholarshipAllocation = convertCentsToDollarsDecimal(data.line_item?.scholarship_allocation ?? 0);
    this.scholarshipPercentage = convertPercentageToDecimal(data.line_item?.scholarship_percentage ?? 0);
    this.twrn = data.twrn;

    // Manually created shouldn't apply to recruits, though the API response
    // will still say it is until a change happens. This is in place to
    // replicate that logic until it's changed in the API.
    this.wasManuallyCreated = data.was_manually_created && isNil(data.recruit_id);

    this.tags = data.tags.reduce<AthleteTag[]>((acc, tagUuid) => {
      const tag = tag_definitions.find((tag) => tag.uuid === tagUuid);
      if (tag) {
        acc.push(new AthleteTag(tag));
      }
      return acc;
    }, []);

    // Ensure equivalency is a number between 0 and 100
    const rawEquivalency = data.scholarship?.equivalency_percentage ?? 0;
    this.equivalencyPercentage = Math.min(Math.max(Number(rawEquivalency) || 0, 0), 100);
    // TODO: Map packageTwrn to scholarship package name using ScholarshipPackageListing data from API
    this.packageTwrn = data.scholarship?.package_twrn ?? null;
    this.awardCategories = data.scholarship?.award_categories ?? [];
    this.scholarshipCalculatedValue = (data.scholarship?.award_categories || []).reduce((acc, category) => acc + (category?.amount ?? 0), 0);
    this.removedFromRosterAt = data.removed_from_roster_at;
    this.axleTwrn = data.axle_twrn;
    this.recruitId = data.recruit_id;
  }

  /**
   * Factory method to create BudgetAthlete from categorized budget data
   * @param athlete - The categorized athlete from the API
   * @param institutionalNilCents - Total institutional NIL in cents (pre-calculated from non-excluded categories)
   * @param thirdPartyNilCents - Total third party NIL in cents (pre-calculated from non-excluded categories)
   * @param tagDefinitions - Tag definitions for mapping tags
   * @param teamTwrn - Team TWRN for position resolution
   */
  static fromCategorized(
    athlete: ApiBudgetAthleteCategorized,
    institutionalNilCents: number,
    thirdPartyNilCents: number,
    tagDefinitions: ApiAthleteTag[],
    teamTwrn?: string,
  ): BudgetAthlete {
    const apiBudgetAthlete: ApiBudgetAthlete = {
      twrn: athlete.twrn,
      first_name: athlete.first_name,
      last_name: athlete.last_name,
      academic_year_start: athlete.academic_year_start,
      academic_year_end: athlete.academic_year_end,
      gender: athlete.gender,
      jersey_number: athlete.jersey_number,
      is_active: athlete.is_active,
      positions: athlete.positions,
      position: athlete.position,
      tags: athlete.tags,
      profile_photo: athlete.profile_photo,
      was_manually_created: athlete.was_manually_created,
      removed_from_roster_at: athlete.removed_from_roster_at,
      axle_twrn: athlete.axle_twrn,
      recruit_id: athlete.recruit_id,
      line_item: {
        allocation: institutionalNilCents,
        created_at: athlete.line_item.created_at,
        has_roster_spot: athlete.line_item.has_roster_spot,
        nil_earnings: thirdPartyNilCents,
        scholarship_allocation: athlete.line_item.scholarship_allocation,
        scholarship_percentage: athlete.line_item.scholarship_percentage,
        updated_at: athlete.line_item.updated_at,
      },
      scholarship: athlete.scholarship,
    };

    return new BudgetAthlete(apiBudgetAthlete, tagDefinitions, teamTwrn);
  }

  getAwardCategoryAmount(twrn: string): number {
    return this.awardCategories.find((cat) => cat.twrn === twrn)?.amount ?? 0;
  }

  getAwardCategoryAmountInDollars(twrn: string): number {
    const amountInCents = this.getAwardCategoryAmount(twrn);
    return convertCentsToDollarsDecimal(amountInCents);
  }

  /**
   * Creates a clone of the current athlete
   */
  clone(): BudgetAthlete {
    // Create a deep clone using lodash
    return cloneDeep(this);
  }

  withUpdatedAwardCategories(newAwardCategories: {
    twrn: string;
    amount: number;
  }[], newPackageTwrn?: string | null): BudgetAthlete {
    const clone = this.clone();

    // Update the clone with new award categories
    clone.awardCategories = newAwardCategories;

    // Update package TWRN if provided
    if (newPackageTwrn !== undefined) {
      clone.packageTwrn = newPackageTwrn;
    }

    // Recalculate scholarship value
    clone.scholarshipCalculatedValue = newAwardCategories.reduce((sum, cat) => sum + cat.amount, 0);

    return clone;
  }

  /**
   * Returns props for AthleteDisplayItem component
   */
  toDisplayProps(options?: {
    academicYear?: AcademicYearEnum | null;
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    layout?: 'row' | 'column';
    onClick?: () => void;
    hideChips?: boolean;
  }): AthleteDisplayItemProps {
    return {
      name: this.fullName,
      avatar: {
        src: this.avatar,
        useInitials: true,
      },
      jerseyNumber: this.jerseyNumber,
      position: this.position,
      academicYear: options?.academicYear ?? null,
      showDeactivatedChip: this.removedFromRosterAt !== null || !this.isActive,
      wasManuallyCreated: this.wasManuallyCreated,
      isRecruit: isAthleteRecruit(this.axleTwrn, this.recruitId),
      size: options?.size ?? 'medium',
      layout: options?.layout ?? 'row',
      onClick: options?.onClick,
      hideChips: options?.hideChips,
    };
  }

  /**
   * Creates a new BudgetAthlete instance by copying all properties from an existing instance.
   * This preserves the class prototype (instanceof checks work) unlike cloneDeep.
   */
  static clone(instance: BudgetAthlete): BudgetAthlete {
    const cloned = Object.create(BudgetAthlete.prototype) as BudgetAthlete;
    Object.assign(cloned, instance);
    // Deep clone arrays to avoid shared references
    cloned.tags = instance.tags.map((tag) => AthleteTag.clone(tag));
    cloned.awardCategories = instance.awardCategories.map((cat) => ({ ...cat }));
    return cloned;
  }
}

export interface ApiEligibleAthlete {
  twrn: string;
  first_name: string;
  last_name: string;
  email: string | null;
  profile_photo_url: string | null;
  jersey_number: string | null;
  position_id: string | null;
  position: string | null;
  academic_year: AcademicYearEnum | null;
  budget_allocation: number | null;
  budget_twrn?: string | null;
  has_tw_profile: boolean;
  is_active: boolean;
  removed_from_roster: boolean;
  tags: AthleteTagBase[];
}

export type IndexTeamAthletesResponse = Omit<ApiEligibleAthlete, 'budget_allocation'> & {
  institutional_nil_categories: ApiBudgetCategories | null;
  third_party_categories: ApiBudgetCategories | null;
  institutional_categories_total: number;
  third_party_categories_total: number;
  recruit_id: number | null;
  axle_twrn: string | null;
};

export interface IndexTeamAthletesParams {
  academic_year_date_id?: string | null;
  has_agreements?: 0 | 1;
  team_budget?: string | null;
  team_budget_group?: string | null;
  only_trashed?: 0 | 1;
  with_trashed?: 0 | 1;
}

export class EligibleAthlete {
  twrn: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string | null;
  profilePhotoUrl: string | null;
  jerseyNumber: string | null;
  position: string | null;
  academicYear: AcademicYearEnum | null;
  budgetAllocation: number | null;
  budgetTwrn: string | null | undefined;
  hasTwProfile: boolean;
  isActive: boolean;
  removedFromRoster: boolean;
  axleTwrn: string | null;
  institutionalNilCategories: ApiBudgetCategories | null = null;
  thirdPartyCategories: ApiBudgetCategories | null = null;
  tags: AthleteTagBase[] | [];
  constructor(
    data: ApiEligibleAthlete | IndexTeamAthletesResponse,
    teamTwrn?: string,
  ) {
    this.twrn = data.twrn;
    this.fullName = `${data.first_name} ${data.last_name}`;
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.email = data.email;
    this.profilePhotoUrl = data.profile_photo_url;
    this.jerseyNumber = data.jersey_number;
    this.tags = data.tags.map((tag) => new AthleteTagBase(tag));

    // Resolve position from UUID, falling back to a pre-resolved name if the caller
    // doesn't have a teamTwrn handy (e.g. building from already-resolved BudgetGroupAthleteItem).
    const resolvedPosition = teamTwrn ? TeamPosition.resolveName(teamTwrn, data.position_id) : null;
    this.position = resolvedPosition ?? data.position ?? null;

    this.academicYear = data.academic_year;
    this.budgetTwrn = data.budget_twrn;
    this.hasTwProfile = data.has_tw_profile;
    this.isActive = data.is_active;
    this.removedFromRoster = data.removed_from_roster;

    this.axleTwrn = 'axle_twrn' in data ? data.axle_twrn : null;

    // Handle data schema-dependent properties
    // Should be simplified when `ApiEligibleAthlete` becomes deprecated
    // and `IndexTeamAthletesResponse` becomes the only option for data
    if ('institutional_categories_total' in data) {
      // Handle new `IndexTeamAthletesResponse` schema
      this.budgetAllocation = convertCentsToDollarsDecimal(data.institutional_categories_total);
      this.institutionalNilCategories = data.institutional_nil_categories;
      this.thirdPartyCategories = data.third_party_categories;
    } else {
      // Handle legacy `ApiEligibleAthlete` schema
      this.budgetAllocation = convertCentsToDollarsDecimal(data.budget_allocation ?? 0);
    }
  }

  /**
   * Returns props for AthleteDisplayItem component
   */
  toDisplayProps(options?: {
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    layout?: 'row' | 'column';
    onClick?: () => void;
    hideChips?: boolean;
  }): AthleteDisplayItemProps {
    return {
      name: this.fullName,
      avatar: {
        src: this.profilePhotoUrl,
        useInitials: true,
      },
      jerseyNumber: this.jerseyNumber,
      position: this.position,
      academicYear: this.academicYear,
      wasManuallyCreated: !this.hasTwProfile,
      showDeactivatedChip: this.removedFromRoster || !this.isActive,
      showRemovedFromBudgetChip: this.budgetTwrn === null,
      size: options?.size ?? 'medium',
      layout: options?.layout ?? 'row',
      onClick: options?.onClick,
      hideChips: options?.hideChips,
      tags: this.tags,
    };
  }
}

export interface ApiCompactAthlete {
  twrn: string;
  first_name: string;
  last_name: string;
  email: string | null;
  profile_photo_url: string | null;
  axle_twrn: string | null;
  has_tw_profile: boolean;
  is_active: boolean;
  gender: Gender;
  wallet_status: WalletStatus;
  wallet_verification_status: WalletVerificationStatus | null;
  is_international: boolean;
  updated_at: string;
  was_manually_created: boolean | null;
  recruit_id: number | null;
  telemetry_player_id: string | null;
}

export class CompactAthlete {
  twrn: string;
  firstName: string;
  lastName: string;
  email: string | null;
  profilePhotoUrl: string | null;
  axleTwrn: string | null;
  hasTwProfile: boolean;
  isActive: boolean;
  gender: Gender;
  walletStatus: WalletStatus;
  walletVerificationStatus: WalletVerificationStatus | null;
  isInternational: boolean;
  updatedAt: string;
  wasManuallyCreated: boolean | null;
  recruitId: number | null;
  telemetryPlayerId: string | null;

  constructor(data: ApiCompactAthlete) {
    this.twrn = data.twrn;
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.email = data.email;
    this.profilePhotoUrl = data.profile_photo_url;
    this.axleTwrn = data.axle_twrn;
    this.hasTwProfile = data.has_tw_profile;
    this.isActive = data.is_active;
    this.gender = data.gender;
    this.walletStatus = data.wallet_status;
    this.walletVerificationStatus = data.wallet_verification_status;
    this.isInternational = data.is_international;
    this.updatedAt = data.updated_at;
    this.wasManuallyCreated = data.was_manually_created;
    this.recruitId = data.recruit_id;
    this.telemetryPlayerId = data.telemetry_player_id;
  }

  static fromApi(data: ApiCompactAthlete): CompactAthlete {
    return new CompactAthlete(data);
  }
}

// Athlete
export interface ApiTeamAthlete {
  twrn: string;
  first_name: string;
  last_name: string;
  email: string | null;
  gender: Gender;
  profile_photo_url: string | null;
  jersey_number: string | null;
  position: string | null;
  position_id: string | null;
  academic_year: AcademicYearEnum | null;
  has_tw_profile: boolean;
  wallet_status: WalletStatus;
  wallet_verification_status: WalletVerificationStatus | null;
  updated_at: string;
  axle_twrn: string | null;
  recruit_id: number | null;
  is_active: boolean;
  removed_from_roster_at: string | null;
  was_manually_created: boolean;
  is_international: boolean;
}

export class TeamAthlete {
  twrn: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  gender: Gender;
  profilePhotoUrl: string | null;
  jerseyNumber: string | null;
  position: string | null;
  position_id: string | null;
  academicYear: AcademicYearEnum | null;
  hasTwProfile: boolean;
  walletStatus: WalletStatus;
  walletVerificationStatus: WalletVerificationStatus | null;
  updatedAt: string;
  isDeactivated = false;
  isManuallyCreated = false;
  isRecruit = false;
  isInternational = false;

  constructor(data: ApiTeamAthlete, teamTwrn?: string) {
    this.twrn = data.twrn;
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.fullName = `${data.first_name} ${data.last_name}`;
    this.email = data.email;
    this.gender = data.gender;
    this.profilePhotoUrl = data.profile_photo_url;
    this.jerseyNumber = data.jersey_number;
    this.position_id = data.position_id;
    this.academicYear = data.academic_year;
    this.hasTwProfile = data.has_tw_profile;
    this.walletStatus = data.wallet_status;
    this.walletVerificationStatus = data.wallet_verification_status;
    this.updatedAt = data.updated_at;
    this.isManuallyCreated = data.was_manually_created;
    this.isDeactivated = !isNil(data.removed_from_roster_at) || data.is_active === false;
    this.isRecruit = isAthleteRecruit(data.axle_twrn, data.recruit_id);
    this.isInternational = data.is_international;

    // Use the position mapping registry to resolve position name from UUID
    this.position = teamTwrn ? TeamPosition.resolveName(teamTwrn, data.position_id) ?? null : data.position;
  }

  /**
   * Returns props for AthleteDisplayItem component
   */
  toDisplayProps(options?: {
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    layout?: 'row' | 'column';
    onClick?: () => void;
    hideChips?: boolean;
  }): AthleteDisplayItemProps {
    return {
      name: this.fullName,
      avatar: {
        src: this.profilePhotoUrl,
        useInitials: true,
      },
      jerseyNumber: this.jerseyNumber,
      position: this.position,
      academicYear: this.academicYear,
      wasManuallyCreated: !this.hasTwProfile,
      isInternational: this.isInternational,
      size: options?.size ?? 'medium',
      layout: options?.layout ?? 'row',
      onClick: options?.onClick,
      hideChips: options?.hideChips,
    };
  }
}

export interface ApiMinimalTeamAthleteSchema {
  academic_year_date_id: string;
  academic_year: AcademicYearEnum | null;
  position_id: string | null;
  jersey_number: string | null;
  removed_at: string | null;
}

export interface ApiTeamAthleteProfile {
  twrn: string;
  first_name: string;
  last_name: string;
  email: string | null;
  gender: Gender;
  profile_photo_url: string | null;
  has_tw_profile: boolean;
  wallet_status: WalletStatus;
  wallet_verification_status: WalletVerificationStatus | null;
  is_active: boolean;
  team_athletes: ApiMinimalTeamAthleteSchema[];
  tags: string[];
  updated_at: string;
  axle_twrn: string | null;
  recruit_id: number | null;
  telemetry_player_id: string | null;
}

export interface TeamAthleteProfileAcademicYear {
  academicYearDateId: string;
  academicYear: AcademicYearEnum | null;
  positionId: string | null;
  jerseyNumber: string | null;
}

export class TeamAthleteProfile {
  twrn: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  gender: Gender;
  profilePhotoUrl: string | null;
  hasTwProfile: boolean;
  walletStatus: WalletStatus;
  walletVerificationStatus: WalletVerificationStatus | null;
  isActive: boolean;
  tags: string[];
  updatedAt: string;
  academicYears: TeamAthleteProfileAcademicYear[];
  activeAcademicYear: TeamAthleteProfileAcademicYear | null = null;
  axleTwrn: string | null;
  recruitId: number | null;
  telemetryPlayerId: string | null;

  constructor(data: ApiTeamAthleteProfile) {
    this.twrn = data.twrn;
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.fullName = `${data.first_name} ${data.last_name}`.trim();
    this.email = data.email;
    this.gender = data.gender;
    this.profilePhotoUrl = data.profile_photo_url;
    this.hasTwProfile = data.has_tw_profile;
    this.walletStatus = data.wallet_status;
    this.walletVerificationStatus = data.wallet_verification_status;
    this.isActive = data.is_active;
    this.tags = data.tags;
    this.updatedAt = data.updated_at;
    this.axleTwrn = data.axle_twrn;
    this.recruitId = data.recruit_id;
    this.telemetryPlayerId = data.telemetry_player_id;

    this.academicYears = data.team_athletes
      .filter((i) => i.removed_at === null)
      .map((i) => ({
        academicYearDateId: i.academic_year_date_id,
        academicYear: i.academic_year,
        positionId: i.position_id,
        jerseyNumber: i.jersey_number,
      }));
  }

  getActiveAcademicYear(allAcademicYears: AcademicYear[]): TeamAthleteProfileAcademicYear | null {
    if (this.activeAcademicYear) return this.activeAcademicYear;

    const currentAcYear = getCurrentAcademicYear(allAcademicYears);

    if (currentAcYear === null) return null;

    let closest: TeamAthleteProfileAcademicYear | null = null;

    for (const acYear of this.academicYears) {
      if (currentAcYear.uuid === acYear.academicYearDateId) {
        return acYear;
      }

      if (closest === null) {
        closest = acYear;
      } else {
        const currentAcademicYear = allAcademicYears.find((ay) => ay.uuid === acYear.academicYearDateId);
        const closestAcademicYear = allAcademicYears.find((ay) => ay.uuid === closest?.academicYearDateId);

        if (currentAcademicYear && closestAcademicYear) {
          const currentStartDate = safeParseDate(currentAcademicYear.startDate);
          const closestStartDate = safeParseDate(closestAcademicYear.startDate);
          const now = new Date();

          if (currentStartDate && closestStartDate) {
            // if this is after the current date, and is closer than closest, set this to closest
            if (currentStartDate > now && closestStartDate > now && currentStartDate < closestStartDate) {
              closest = acYear;
            } else if (currentStartDate > now && closestStartDate <= now) {
              // if this is after current date but closest is before, prefer this (future over past)
              closest = acYear;
            } else if (currentStartDate <= now && closestStartDate <= now && currentStartDate > closestStartDate) {
              // else if this is before the current date and more recent than closest, set this to closest
              closest = acYear;
            }
          }
        }
      }
    }

    this.activeAcademicYear = closest;
    return closest;
  }

  /**
   * Convert to AthleteDisplayItem props
   */
  toDisplayProps(options?: {
    athleteYear?: TeamAthleteProfileAcademicYear | null;
    position?: string | null;
    org?: string | null;
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    layout?: 'row' | 'column';
    onClick?: () => void;
  }): AthleteDisplayItemProps {
    return {
      name: this.fullName,
      avatar: {
        src: this.profilePhotoUrl,
        useInitials: true,
        size: options?.size === 'large' ? 48 : options?.size === 'xlarge' ? 56 : undefined,
      },
      jerseyNumber: options?.athleteYear?.jerseyNumber ?? null,
      position: options?.position ?? null,
      academicYear: options?.athleteYear?.academicYear ?? null,
      org: options?.org ?? null,
      isActive: this.isActive,
      isRecruit: isAthleteRecruit(this.axleTwrn, this.recruitId),
      size: options?.size ?? 'medium',
      layout: options?.layout ?? 'row',
      onClick: options?.onClick,
    };
  }
}

export type MinimalAthleteForPosition = Pick<TeamAthlete, 'twrn' | 'fullName' | 'profilePhotoUrl'> | Pick<TeamAthleteProfile, 'twrn' | 'fullName' | 'profilePhotoUrl'>;

export interface ApiTeamAthleteListingSortParams {
  field?: 'full_name' | 'academic_year';
  direction?: 'asc' | 'desc';
}

export type ApiTeamAthleteListingRequestBody = Omit<ApiPaginationParams, 'total'> & {
  keyword?: string | null;
  academic_year: string;
  sort?: ApiTeamAthleteListingSortParams;
  with_trashed?: boolean;
};

export type ApiTeamAthleteExportRequestBody = Pick<
  ApiTeamAthleteListingRequestBody,
  'keyword' | 'academic_year' | 'with_trashed'
>;


export type RemoveAthleteRequestModel = {
  academic_year: string; // academic year uuid
  removal_type: AthleteRemovalTypeEnum;
};

export type RestoreDeletedAthleteRequestModel = {
  athletes: string[];
};

export const ATHLETE_POSITION_UPDATE_TYPE_ENUM = {
  CURRENT: 'current',
  FUTURE_AND_CURRENT: 'future_and_current',
  FUTURE: 'future',
} as const;

export type AthletePositionUpdateTypeEnum
  = typeof ATHLETE_POSITION_UPDATE_TYPE_ENUM[keyof typeof ATHLETE_POSITION_UPDATE_TYPE_ENUM];

/**
 * Determines if an athlete is a recruit.
 * An athlete is considered a recruit if they have a recruit_id but no axle_twrn.
 *
 * @param axleTwrn - The athlete's Axle TWRN (from Teamworks profile)
 * @param recruitId - The athlete's recruit ID (from C&R)
 * @returns true if the athlete is a recruit, false otherwise
 */
export function isAthleteRecruit(axleTwrn: string | null | undefined, recruitId: number | null | undefined): boolean {
  return !axleTwrn && !!recruitId;
}
